<script setup>
import moment from "moment/moment";
import { onMounted, ref, computed } from "vue";
import CustomerRequests from "../works/CustomerRequests.vue";
import WorksTable from "./WorksTable.vue";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import axios from "axios";
import {dateTime} from "@/helpers/dateTimeHelpers.js";
import {useDefaultsListStore} from "@/stores/defaultsListStore.js";
import {storeToRefs} from "pinia";
import T1WorkOrders from "@/components/home/dashboard/works/T1WorkOrders.vue";

const props = defineProps(["permissions"]);
const defaultListsStore = useDefaultsListStore()
const {defaults} = storeToRefs(defaultListsStore)
const {updateStoreFields}= defaultListsStore


const page = ref(1);
const works = ref([]);
const users = ref(null);
const teams = ref(null);
const recentWorks = ref([]);
const suburbs = ref([]);
const equipment = ref([]);
const zones = ref([]);
const tags = ref([]);
const tasks = ref(null);

const crms = ref(null);
const wos = ref(null);
const loading = ref(false);
const today = ref(moment().format("dddd Do MMM YYYY"));
const unlinkedTrees = ref([]);

const downloading= ref(false);
const downloadCsvId = ref(null);
const downloadFilename = ref(null);

const downloadData = () => {
    window.open("/api/works/export");
};

const downloadCSV = () => {
    window.location.href = "/api/reporting/csv/download/" + downloadFilename.value;
};

const refreshData = () => {
    getScheduledInspections();
};

const downloadWorksCSV = () =>{
    downloadFilename.value = null;
    downloadCsvId.value = null;
    downloading.value = true;
    axios.get("/api/works/export").then(response => {
        downloadCsvId.value = response.data.csv.id;
        checkDownloads();
    }).catch((error) => {
        console.log(error)
    });
};

const checkDownloads = () =>{
    axios
        .get("api/assign/mine/check/" + downloadCsvId.value)
        .then((response) => {
            if (response.data.csv.status === "pending") {
                setTimeout(() => {
                    checkDownloads();
                }, 5000);
            } else {
                downloadFilename.value = response.data.csv.filename;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

const getScheduledInspections = () => {
    loading.value = true;
    axios
        .get("/api/maintenance/trees")
        .then((response) => {
            loading.value = false;
            tasks.value = response.data.workTasks;
            crms.value = response.data.crms;
            tags.value = response.data.tags;
            zones.value = response.data.zones;

            works.value = response.data.works.filter((w) => !w.work_orders);

            wos.value = response.data.works.filter((w) => w.work_orders);

            unlinkedTrees.value = response.data.t1WoNoTree;

            recentWorks.value = response.data.recentlyComplete;
            users.value = response.data.users;
            teams.value = response.data.teams;
            updateStoreFields(response.data.defaults)

            getSuburbs();
            getEquipment();
        })
        .catch((error) => {
            console.log(error);
        });
};

// Function to get suburbs
const getSuburbs = () => {
    suburbs.value = [];
    works.value.map((work) => {
        if (work.tree && work.city) {
            if (!suburbs.value.includes(work.city)) {
                suburbs.value.push(work.city);
            }
        }
    });
    suburbs.value.sort();
};

// Function to get equipment
const getEquipment = () => {
    equipment.value = [];
    works.value.map((work) => {
        if (work.equipment.length > 0) {
            work.equipment.map((e) => {
                if (!equipment.value.includes(e.name)) {
                    equipment.value.push(e.name);
                }
            });
        }
    });
    equipment.value.sort();
};

// Add computed property to filter tasks
const getFilteredTasks = (work) => {
    return work.works_as_list.filter(task => forestreeHelpers.getWorkTask(task, tasks.value));
};


// Call the function when the component is mounted
onMounted(() => {
    getScheduledInspections();
});
</script>

<template>
    <div class="flex flex-wrap lg:flex-no-wrap w-full lg:w-3/4 lg:p-4">
        <div class="h-auto bg-white forestree-box all-rounded w-full lg:ml-6">
            <div class="bg-white rounded shadow-sm px-1 lg:px-8 mt-6 pb-6">
                <div class="flex flex-wrap justify-between lg:flex-no-wrap my-6 py-4 w-full border-b border-slate-300">
                    <div class="flex pb-6 md:pb-0">
                        <span
                            @click.prevent="page = 1"
                            :class="page === 1 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                            class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                        >
                            Open Works ({{ works.length }})
                        </span>
                        <span
                            v-if="crms && defaults && (defaults.spreadsheet_crms || defaults.pathway_integration || defaults.authority_integration)"
                            @click.prevent="page = 2"
                            :class="page === 2 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                            class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                        >
                            Customer Requests ({{ crms.length }})
                        </span>
                        <span
                            v-if="wos && defaults.techone?.active"
                            @click.prevent="page = 4"
                            :class="page === 4 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                            class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                        >
                            T1 Work Orders ({{ wos.length + unlinkedTrees.length }})
                        </span>
                        <span
                            @click.prevent="page = 3"
                            :class="page === 3 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                            class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                        >
                            Completed Works
                        </span>
                    </div>
                    <div class="pt-3 md:pt-0">
                        <span
                            v-if="page === 1"
                            @click.prevent="downloadWorksCSV()"
                            class="w-full text-center md:w-auto p-3 font-semibold cursor-pointer hover:text-blue-600 text-blue-700 rounded-lg bg-slate-100"
                        >
                            CSV Export
                        </span>
                    </div>

                    <div v-if="downloading" class="relative z-40" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div v-if="downloadFilename" class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                            </svg>
                                        </div>
                                        <div v-else class="w-full text-center flex justify-center">
                                            <div class="csvloader">
                                                Creating CSV...
                                            </div>
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <h3 v-if="downloadFilename" class="text-base font-semibold text-gray-900" id="modal-title">CSV Created</h3>
                                            <h3 v-else class="text-base font-semibold text-gray-900" id="modal-title">Creating CSV</h3>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex justify-between">
                                        <button @click="downloading = false" type="button" class="w-32 inline-flex justify-center rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Close</button>
                                        <button :disabled="!downloadFilename" @click="downloadCSV()" type="button" :class="downloadFilename ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-200'" class="w-32 inline-flex justify-center rounded-lg  px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">Download</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div v-if="page === 1">
                    <div v-if="loading" class="w-full h-56 text-center">
                        <img src="/loading.gif" width="60" class="mx-auto" />
                        <h3 class="text-xl text-slate-500 leading-none mb-3">Growing your Works...</h3>
                        <h3 class="text-lg text-slate-500 leading-none mb-3">Please wait</h3>
                    </div>

                    <works-table
                        v-if="defaults"
                        :works="works"
                        :suburbs="suburbs"
                        :equipment="equipment"
                        :users="users"
                        :teams="teams"
                        :tasks="tasks"
                        :permissions="permissions"
                        :tags="tags"
                        :defaults="defaults"
                        :refreshData="refreshData"
                        :zones="zones"
                    />
                </div>

                <div v-if="page === 2">
                    <customer-requests :requests="crms" :equipment="equipment" :users="users" :teams="teams" :permissions="permissions" :defaults="defaults" />
                </div>

                <div v-if="page === 4">
                    <t-1-work-orders
                        :works="wos"
                        :suburbs="suburbs"
                        :equipment="equipment"
                        :users="users"
                        :teams="teams"
                        :tasks="tasks"
                        :permissions="permissions"
                        :unlinkedTrees="unlinkedTrees"
                        :refresh-data="refreshData"/>
                </div>

                <div v-if="page === 3">
                    <div class="flex">
                        <div class="lg:w-1/2">
                            <p class="my-3 text-xl text-slate-600">
                                Recently Completed Works
                                <span v-if="recentWorks">({{ recentWorks.length }})</span>
                            </p>
                        </div>
                        <div class="lg:w-1/2 text-right text-slate-600"></div>
                    </div>

                    <div class="ftree-table striped hovered main-first-cell w-full overflow-x-auto">
                        <div class="thead">
                            <div class="tr py-2">
                                <div class="td items-center w-1/2 lg:w-1/6">Tree ID</div>
                                <div class="td items-center w-1/2 lg:w-2/6">Tasks Completed</div>
                                <div class="td items-center w-1/2 lg:w-2/6">Notes</div>
                                <div class="td items-center w-1/2 lg:w-1/6">User</div>
                            </div>
                        </div>
                        <div class="tbody text-left text-slate-700">
                            <div v-for="(work, index) in recentWorks" :key="index" class="tr border-b border-slate-200">
                                <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                    <a :href="'/maintenance-map?treeId=' + work.tree_id" class="text-slate-600 font-bold mr-3">#{{ work.tree_id }}</a>
                                    <span v-if="work.crm" class="inline-block bg-red-400 rounded-full px-3 py-1 text-sm text-slate-100 mr-2">
                                        {{ work.crm.crm_id }}
                                    </span>
                                </div>
                                <div v-if="work.work_tasks_done" class="td py-2 w-1/2 lg:w-2/6 items-center">
                                    <div v-if="work.works_as_list.length" class="py-1">
                                        <span
                                            v-for="(task, index) in getFilteredTasks(work)"
                                            :key="index"
                                            class="inline-block bg-green-200 rounded-full px-3 py-0.5 text-sm text-green-700 font-semibold mr-2 mb-1"
                                        >
                                            {{ forestreeHelpers.getWorkTask(task, tasks) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="td py-2 w-1/2 lg:w-2/6 items-center text-slate-500 text-sm">
                                    {{ work.notes }}
                                </div>
                                <div class="td td-block-wrap py-2 w-1/2 lg:w-1/6 items-center text-slate-500">
                                    <div>
                                        <div class="pt-1 w-full text-sm font-semibold">
                                            {{ work.name }}
                                        </div>
                                        <div class="pb-1 w-full text-xs">
                                            {{ dateTime(work.updated_at) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
